/**
 * Focus management utilities for handling accessibility issues with modals and overlays.
 * Provides functions to ensure proper focus handling when aria-hidden is applied to elements.
 * Update when modifying modal behavior or adding new accessibility requirements.
 */
import { useEffect } from 'react';

/**
 * Ensures that no focusable elements retain focus when aria-hidden is applied to their ancestors.
 * This prevents the accessibility violation where focused elements are hidden from assistive technology.
 */
const manageFocusForAriaHidden = (isModalOpen: boolean): void => {
  if (isModalOpen) {
    // Find the currently focused element
    const activeElement = document.activeElement as HTMLElement | null;

    // Check if the active element or any of its ancestors has aria-hidden="true"
    if (activeElement && isElementOrAncestorAriaHidden(activeElement)) {
      // Move focus to the modal or a safe focusable element
      const modal = document.querySelector<HTMLElement>(
        '[role="dialog"], .MuiDrawer-modal, .MuiModal-root'
      );
      if (modal) {
        const firstFocusableElement = findFirstFocusableElement(modal);
        if (firstFocusableElement) {
          firstFocusableElement.focus();
        }
      } else {
        // Fallback: blur the current element
        activeElement.blur();
      }
    }
  }
};

/**
 * Checks if an element or any of its ancestors has aria-hidden="true"
 */
const isElementOrAncestorAriaHidden = (element: Element): boolean => {
  let current: Element | null = element;
  while (current && current !== document.body) {
    if (current.getAttribute('aria-hidden') === 'true') {
      return true;
    }
    current = current.parentElement;
  }
  return false;
};

/**
 * Finds the first focusable element within a container
 */
const findFirstFocusableElement = (container: Element): HTMLElement | null => {
  const focusableSelectors = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  return container.querySelector<HTMLElement>(focusableSelectors);
};

/**
 * Custom hook for managing focus in modal components
 */
export const useFocusManagement = (isOpen: boolean) => {
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM updates are complete
      const timeoutId = setTimeout(() => {
        manageFocusForAriaHidden(true);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  return {
    manageFocus: () => manageFocusForAriaHidden(isOpen),
  };
};
